(() => {
  'use strict';

  const WIDTH = 2048;
  const HEIGHT = 1365;
  const PROPERTY_NAME = 'properties[_Text PNG]';
  const GENERATED_ATTRIBUTE = 'data-crystal-text-png-field';
  const FONT_FAMILIES = {
    Georgia: "Georgia, 'Times New Roman', serif",
    Arial: 'Arial, Helvetica, sans-serif',
    'Times New Roman': "'Times New Roman', Times, serif",
    'Brush Script MT': "'Brush Script MT', 'Segoe Script', cursive"
  };

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const canvasBlob = (canvas) => new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Unable to create the text PNG.'));
    }, 'image/png');
  });

  const wrapCanvasText = (context, value, maxWidth) => value.split('\n').flatMap((paragraph) => {
    if (!paragraph) return [''];
    const hasSpaces = paragraph.includes(' ');
    const units = hasSpaces ? paragraph.split(/\s+/) : [...paragraph];
    const separator = hasSpaces ? ' ' : '';
    const lines = [];
    let line = '';
    units.forEach((unit) => {
      const candidate = line ? `${line}${separator}${unit}` : unit;
      if (line && context.measureText(candidate).width > maxWidth) {
        lines.push(line);
        line = unit;
      } else {
        line = candidate;
      }
    });
    if (line) lines.push(line);
    return lines;
  });

  const init = (root) => {
    if (!root || root.dataset.crystalTextPngReady === 'true') return;
    const form = root.querySelector('.cp__form');
    const engravingText = root.querySelector('[data-engraving-text]');
    const engravingFont = root.querySelector('[data-engraving-font]');
    const engravingFeature = root.querySelector('[data-feature-key="text"]');
    const engravingSize = root.querySelector('[data-engraving-size]');
    const engravingX = root.querySelector('[data-engraving-x]');
    const engravingY = root.querySelector('[data-engraving-y]');
    const engravingRotation = root.querySelector('[data-engraving-rotation]');
    if (!form || !engravingText || !engravingFeature) return;

    root.dataset.crystalTextPngReady = 'true';
    const status = root.querySelector('[data-status]');
    const editParams = new URLSearchParams(window.location.search);
    const isFullEdit = editParams.get('ag_mode') === 'full' && Boolean(editParams.get('ag_edit'));
    let cachedSignature = '';
    let cachedFile = null;
    let pendingSignature = '';
    let pendingPromise = null;
    let generation = 0;
    let debounceTimer = 0;

    const readState = () => ({
      enabled: Boolean(engravingFeature.checked),
      text: String(engravingText.value || '').trim(),
      font: String(engravingFont?.value || 'Georgia'),
      size: clamp(Number(engravingSize?.value || 32), 14, 72),
      x: clamp(Number(engravingX?.value || 0), -38, 38),
      y: clamp(Number(engravingY?.value || 0), -38, 38),
      rotation: clamp(Number(engravingRotation?.value || 0), -180, 180)
    });

    const signatureFor = (state) => JSON.stringify(state);

    const clearGeneratedFields = () => {
      form.querySelectorAll(`[${GENERATED_ATTRIBUTE}]`).forEach((node) => node.remove());
    };

    const attachNoneForFullEdit = () => {
      clearGeneratedFields();
      if (!isFullEdit) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = PROPERTY_NAME;
      input.value = 'None';
      input.setAttribute(GENERATED_ATTRIBUTE, 'empty');
      form.append(input);
    };

    const attachFile = (file) => {
      const transfer = new DataTransfer();
      transfer.items.add(file);
      const input = document.createElement('input');
      input.type = 'file';
      input.name = PROPERTY_NAME;
      input.hidden = true;
      input.tabIndex = -1;
      input.files = transfer.files;
      input.setAttribute(GENERATED_ATTRIBUTE, 'file');
      clearGeneratedFields();
      form.append(input);
    };

    const renderFile = async (state) => {
      const canvas = document.createElement('canvas');
      canvas.width = WIDTH;
      canvas.height = HEIGHT;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Your browser could not prepare the text PNG.');

      const fontFamily = FONT_FAMILIES[state.font] || FONT_FAMILIES.Georgia;
      const fontSize = state.size * (WIDTH / 620);
      await document.fonts?.load(`600 ${fontSize}px ${fontFamily}`, state.text).catch(() => {});

      context.fillStyle = '#000000';
      context.fillRect(0, 0, WIDTH, HEIGHT);
      context.font = `600 ${fontSize}px ${fontFamily}`;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillStyle = '#FFFFFF';

      const lines = wrapCanvasText(context, state.text, WIDTH * 0.62);
      const lineHeight = fontSize * 1.16;
      context.save();
      context.translate(WIDTH / 2 + (state.x / 100) * WIDTH, HEIGHT / 2 + (state.y / 100) * HEIGHT);
      context.rotate((state.rotation * Math.PI) / 180);
      lines.forEach((line, index) => {
        const y = (index - (lines.length - 1) / 2) * lineHeight;
        context.fillText(line, 0, y, WIDTH * 0.62);
      });
      context.restore();

      const blob = await canvasBlob(canvas);
      return new File([blob], 'text-white-on-black.png', { type: 'image/png' });
    };

    const materialize = async () => {
      const state = readState();
      const signature = signatureFor(state);
      if (!state.enabled || !state.text) {
        generation += 1;
        cachedSignature = signature;
        cachedFile = null;
        pendingSignature = '';
        pendingPromise = null;
        attachNoneForFullEdit();
        return null;
      }
      if (cachedSignature === signature && cachedFile) {
        if (!form.querySelector(`[${GENERATED_ATTRIBUTE}="file"]`)) attachFile(cachedFile);
        return cachedFile;
      }
      if (pendingSignature === signature && pendingPromise) return pendingPromise;

      const currentGeneration = ++generation;
      pendingSignature = signature;
      pendingPromise = renderFile(state).then((file) => {
        if (currentGeneration !== generation || signatureFor(readState()) !== signature) return null;
        cachedSignature = signature;
        cachedFile = file;
        attachFile(file);
        root.dataset.crystalTextPngBytes = String(file.size);
        return file;
      }).finally(() => {
        if (pendingSignature === signature) {
          pendingSignature = '';
          pendingPromise = null;
        }
      });
      return pendingPromise;
    };

    const scheduleMaterialize = () => {
      window.clearTimeout(debounceTimer);
      cachedSignature = '';
      cachedFile = null;
      clearGeneratedFields();
      const state = readState();
      if (!state.enabled || !state.text) {
        attachNoneForFullEdit();
        return;
      }
      debounceTimer = window.setTimeout(() => {
        materialize().catch((error) => console.error('[ArtLaser3D text PNG]', error));
      }, 180);
    };

    [engravingText, engravingFont, engravingFeature, engravingSize, engravingX, engravingY, engravingRotation]
      .filter(Boolean)
      .forEach((input) => {
        input.addEventListener('input', scheduleMaterialize);
        input.addEventListener('change', scheduleMaterialize);
      });

    form.addEventListener('submit', async (event) => {
      const state = readState();
      const signature = signatureFor(state);
      const ready = cachedSignature === signature && (
        (!state.enabled || !state.text)
          ? (!isFullEdit || Boolean(form.querySelector(`[${GENERATED_ATTRIBUTE}="empty"]`)))
          : Boolean(cachedFile && form.querySelector(`[${GENERATED_ATTRIBUTE}="file"]`))
      );
      if (ready) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      const submitter = event.submitter;
      try {
        await materialize();
        if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter || undefined);
        else form.dispatchEvent(new SubmitEvent('submit', { bubbles: true, cancelable: true, submitter }));
      } catch (error) {
        console.error('[ArtLaser3D text PNG]', error);
        if (status) status.textContent = error?.message || 'We could not prepare the text PNG. Please try again.';
      }
    }, true);

    scheduleMaterialize();
  };

  document.querySelectorAll('[data-crystal-personalizer]').forEach(init);
  document.addEventListener('shopify:section:load', (event) => {
    init(event.target.querySelector('[data-crystal-personalizer]'));
  });
})();
